import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "~constants/pagination.ts";
import { PermissionNames } from "~enums/permissions.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { useVenueService } from "~pages/Venues/hooks/useVenueService.ts";
import { defaultVenuesExportFieldNames, venuesExportFields } from "~pages/Venues/VenueListPage/constants/exportFields.ts";
import { useFetchVenues } from "~pages/Venues/VenueListPage/hooks/useFetchVenues.ts";
import { useGetVenueTableColumns } from "~pages/Venues/VenueListPage/hooks/useGetVenueTableColumns.tsx";
import { useGetVenueTableFilters } from "~pages/Venues/VenueListPage/hooks/useGetVenueTableFilters.tsx";

const VenueListPage = () => {
  const paginationConfig = useHandlePagination(20, 1, DEFAULT_PAGE_SIZE_OPTIONS);
  const { sport, customerId, organizationId } = useParams();
  const { formatMessage } = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const handleAdd = () => navigate(`/${customerId}/admin/${sport}/${organizationId}/venues/manage/`);
  const [searchText, setSearchText] = useState(location.state?.search || "");

  const { uiConfiguration } = useSportConfiguration();
  const dataTableUiConfiguration = uiConfiguration.pages.venues.listPage;
  const dataTableImportConfiguration = uiConfiguration.pages.venues.listPage.importConfig;
  const [sortBy, setSortBy] = useState<string | null>(null);
  const filterConfig = useGetVenueTableFilters();

  const { data, refetch, isPending } = useFetchVenues({
    enabled: sortBy !== null,
    customParams: {
      limit: paginationConfig.pageSize.toString(),
      offset: paginationConfig.offset.toString(),
      sortBy: sortBy || "",
      status: filterConfig.currentFilter.status?.value,
      countryCode: filterConfig.currentFilter.countryCode?.value,
      nameLocalContains: searchText,
    },
  });

  const { deleteVenue, isDeletePending, importVenues, exportVenues, isExportPending } = useVenueService();

  const handleDelete = useCallback(
    async (venueId: string) => {
      return await deleteVenue(venueId);
    },
    [deleteVenue],
  );

  const columns = useGetVenueTableColumns();

  const dataSource = data?.data || [];

  const getNavigateUrl = (venueId: string) => `/${customerId}/admin/${sport}/${organizationId}/venues/manage/${venueId}`;

  return (
    <DataTable
      handleRefresh={async () => {
        await refetch();
      }}
      title={formatMessage({ id: "venuesListPage.title" })}
      columns={columns}
      rowActions={{
        base: {
          delete: {
            onClick: (row) => handleDelete(row.venueId),
            loading: isDeletePending,
          },
          manage: {
            onClick: (row) => navigate(getNavigateUrl(row.venueId)),
          },
        },
      }}
      rowKey={"venueId"}
      rowNameKey={"nameLocal"}
      dataSource={dataSource}
      columnChooserLocalStorageKey={`${sport}.tables.settings.columns.venues`}
      isDataLoading={isPending}
      handleAdd={handleAdd}
      addButtonIntlKey={"venue"}
      filtersConfig={filterConfig}
      searchConfig={{
        handleSearch: setSearchText,
        searchText: searchText,
      }}
      columnSortConfig={{
        sorterLocalStorageKey: `${sport}.tables.settings.sort.venues`,
        defaultSorter: {
          columnKey: "nameLocal",
          order: "descend",
        },
        onSortChange: setSortBy,
      }}
      exportConfig={{
        handleExport: exportVenues,
        loading: isExportPending,
        defaultFields: defaultVenuesExportFieldNames,
        groupedFields: venuesExportFields,
        localStorageKey: "export.venues",
      }}
      importConfig={{
        ...dataTableImportConfiguration,
        handleImport: importVenues,
      }}
      permissionNames={[PermissionNames.VENUE]}
      dataTableUiConfiguration={dataTableUiConfiguration}
      paginationConfig={{ ...paginationConfig, hasNext: data?.hasNextLink }}
    />
  );
};

export default VenueListPage;
